import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui";
import { describePhase1Progress } from "../lib/phase1Progress";
import { phase1CurrentActivityLine, phase1OverallPercent } from "../lib/phase1OverallPercent";
import { Icon } from "./Icon";
import { Phase1ProgressBar } from "./Phase1ProgressBar";
import type { Phase1JobPoll } from "../lib/phase1Job";

type Props = {
  runId: string;
  targetName?: string;
  jobStatus?: string;
  statusDetail?: string;
  progress: Phase1JobPoll["progress"] | null;
  error?: string;
  expanded: boolean;
  onToggle: () => void;
  canStop?: boolean;
  stopping?: boolean;
  onStop?: () => void;
  children?: ReactNode;
};

export function Phase1ProgressDock({
  runId,
  targetName = "—",
  jobStatus = "",
  statusDetail = "",
  progress,
  error,
  expanded,
  onToggle,
  canStop = false,
  stopping = false,
  onStop,
  children,
}: Props) {
  const copy = describePhase1Progress({
    status: jobStatus,
    status_detail: statusDetail,
    progress,
  });
  const activityLine =
    copy.activityLine || phase1CurrentActivityLine(progress) || copy.detail;
  const overallPct = phase1OverallPercent(progress);
  const showBar = !error && copy.phaseLabel !== "queued";

  return (
    <div
      className={`shrink-0 border-t border-outline-variant bg-surface-container flex flex-col ${
        expanded ? "shadow-[0_-4px_24px_rgba(0,0,0,0.35)] max-h-[min(85vh,720px)]" : ""
      }`}
    >
      <div className="flex items-stretch border-b border-outline-variant/40">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 px-4 py-3 flex items-center gap-3 text-left hover:bg-surface-container-high transition-colors"
          aria-expanded={expanded}
        >
          {!error && <span className="orion-spinner w-5 h-5 shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-on-surface truncate">
              {error ? "Phase 1 failed" : "Phase 1 running"}
              {targetName !== "—" ? (
                <span className="font-normal text-on-surface-variant"> · {targetName}</span>
              ) : null}
            </p>
            <p className="text-xs font-data-mono text-on-surface-variant truncate">
              {error || activityLine || "Discovering and downloading evidence…"}
              {expanded ? " · click to collapse" : " · click to expand live progress"}
            </p>
            {showBar ? (
              <div className="mt-2 max-w-xl flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <Phase1ProgressBar percent={overallPct} compact label="" />
                </div>
                <span className="text-[10px] text-on-surface-variant shrink-0 tabular-nums">
                  {Math.round(overallPct)}%
                </span>
              </div>
            ) : null}
          </div>
          <Link
            to={`/logs?run=${encodeURIComponent(runId)}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary text-[10px] uppercase tracking-widest hover:underline shrink-0 px-2"
          >
            Log
          </Link>
          <Icon
            name={expanded ? "expand_more" : "expand_less"}
            className="text-on-surface-variant shrink-0"
          />
        </button>
        {canStop && onStop ? (
          <Button
            variant="danger"
            onClick={onStop}
            disabled={stopping}
            className="shrink-0 rounded-none h-full px-4 py-3 border-l border-outline-variant bg-error/10 hover:bg-error/20"
            title="Stop this Phase 1 run"
          >
            {stopping ? "Stopping…" : "Stop job"}
          </Button>
        ) : null}
      </div>
      {expanded ? (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">{children}</div>
      ) : null}
    </div>
  );
}
